import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateAdapter,
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule,
} from '@angular-material-components/datetime-picker';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { DrawerWorkorderActionsComponent } from '../../drawers/drawer-workorder-actions/drawer-workorder-actions.component';
import { EmployeesRepository } from 'src/employees/employees.repository';
import { EmployeesQuery } from 'src/employees/state/employees.query';
import { WorkOrdersRepository } from '../../workorders.repository';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { EmployeeDto, EmployeeVisitDto, WorkOrderDto } from 'src/api';
import { DateService } from 'src/app/shared/services/date.service';
import { ComparingDatesEnum } from 'src/app/shared/enums/comparingDates.enum';
import { EmployeeVisit } from '../employee-visit';
import { EmployeeVisitsView } from '../employee-visits-view-enum';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { TranslateModule } from '@ngx-translate/core';
import { LocalizedDatePipe } from '../../../app/shared/localizedDatePipe';
import { CustomDateAdapter, MY_DATE_FORMATS } from 'src/app/shared/customDateAdapter';
@Component({
  selector: 'app-workorder-create-employee-visits-wizard',
  standalone: true,
  providers: [
    { provide: NGX_MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: NgxMatDateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './create-employee-visits-wizard.component.html',
  styleUrls: ['./create-employee-visits-wizard.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    DrawerWorkorderActionsComponent,
    TranslateModule,
    LocalizedDatePipe,
  ],
})
export class CreateEmployeeVisitsWizardComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @Input({ required: true }) workOrder!: WorkOrderDto;
  @Output() employeeVisitsViewChange = new EventEmitter<EmployeeVisitsView>();
  @Output() enableSaveButton = new EventEmitter<boolean>();
 
  availableEmployees$: Observable<EmployeeDto[]>;
  searchedEmployeeControl = new FormControl<EmployeeDto>({});
 
  searchTerm: BehaviorSubject<string> = new BehaviorSubject<string>('');
  searchTerm$: Observable<string> = this.searchTerm.asObservable();
 
  isSelectEmployeesStepDone = false;
  isStartAndEndDateOnDifferentDays = false;
 
  maxStartDate: Date = new Date();
  maxEndDate: Date = new Date();
  startDateControl = new FormControl(new Date());
  endDateControl = new FormControl(new Date());
 
  employeeVisits: EmployeeVisit[] = [];
 
  constructor(
    private readonly employeesRepository: EmployeesRepository,
    private readonly employeesQuery: EmployeesQuery,
    private readonly workOrderRepository: WorkOrdersRepository
  ) {
    this.availableEmployees$ = combineLatest([this.employeesQuery.selectAll(), this.searchTerm$]).pipe(
      map(([employees, searchTerm]) => {
        if (searchTerm) {
          return employees.filter(
            (e) =>
              this.workOrder.specialistIds?.includes(e.id as number) &&
              e.name?.toLowerCase().includes(searchTerm.toLocaleLowerCase())
          );
        }
        return [];
      })
    );
  }
 
  ngOnInit() {
    this.maxEndDate = DateService.createDateWithAddedDays(this.maxStartDate, 1);
    this.endDateControl.value!.setHours(this.endDateControl.value!.getHours() + 1);
    this.startDateControl.value!.setMinutes(0);
    this.endDateControl.value!.setMinutes(0);
 
    this.employeesRepository.loadEmployees();
  }
 
  changeCalendarDate() {
    this.maxEndDate = DateService.createDateWithAddedDays(this.startDateControl.value!, 1);
 
    const isEndDateBiggerThanMaxDate = DateService.compareDatesWhitoutTime(
      this.endDateControl.value!,
      this.maxEndDate,
      ComparingDatesEnum.Bigger
    );
 
    if (isEndDateBiggerThanMaxDate || this.startDateControl.value! > this.endDateControl.value!) {
      this.endDateControl.setValue(this.startDateControl.value!);
    }
 
    this.isStartAndEndDateOnDifferentDays = DateService.compareDatesWhitoutTime(
      this.endDateControl.value!,
      this.startDateControl.value!,
      ComparingDatesEnum.Bigger
    );
 
    if (this.employeeVisits.length > 0) {
      this.employeeVisits.map((e) => {
        this.updateEmployeeVisitDates(e, this.startDateControl.value!, this.endDateControl.value!);
      });
    }
  }
 
  updateSearchTerm(searchTerm: string) {
    this.searchTerm.next(searchTerm);
  }
 
  displayEmployee(employee: EmployeeDto): string {
    return employee && employee.name ? employee.name : '';
  }
 
  assignEmployeeToVisit() {
    const searchedEmployee = this.searchedEmployeeControl.value;
    if (searchedEmployee) {
      const startDate = this.startDateControl.value!;
      const endDate = this.endDateControl.value!;
 
      const selectedEmployee = {
        startDate: startDate,
        endDate: endDate,
        employeeId: searchedEmployee.id,
        employeeFullName: searchedEmployee.name ? searchedEmployee.name : '',
        employeeAbbreviatedName: searchedEmployee.name ? this.getAbbreviatedName(searchedEmployee.name) : '',
        display: true,
      } as EmployeeVisit;
 
      this.employeeVisits.push(selectedEmployee);
      this.isSelectEmployeesStepDone = true;
      this.searchedEmployeeControl.reset();
    }
  }
 
  getAbbreviatedName(name: string) {
    const nameArray = name.split(' ');
    return nameArray.length > 1 ? `${nameArray[0]} ${nameArray[1].charAt(0)}.` : nameArray[0];
  }
 
  isAlreadySelected(employee: EmployeeDto) {
    return this.employeeVisits.some((e) => e.employeeId === employee.id);
  }
 
  removeEmployeeVisit(employeeId: number) {
    if (employeeId) {
      this.employeeVisits = this.employeeVisits.filter((ev) => ev.employeeId != employeeId);
      if (this.employeeVisits.length == 0) {
        this.isSelectEmployeesStepDone = false;
      }
    }
  }
 
  changeEmployeeStartTime(startTime: string, employeeVisit: EmployeeVisit) {
    const splitTime = startTime.split(':');
    let startDate = DateService.createNewDateWithTime(
      employeeVisit.startDate,
      parseInt(splitTime[0]),
      parseInt(splitTime[1])
    );
 
    let endDate = employeeVisit.endDate;
    if (startDate > endDate) {
      endDate = startDate;
    }
 
    this.updateEmployeeVisitDates(employeeVisit, startDate, endDate);
  }
 
  changeEmployeeEndTime(endTime: string, employeeVisit: EmployeeVisit) {
    const splitTime = endTime.split(':');
    let startDate = employeeVisit.startDate;
    let endDate = DateService.createNewDateWithTime(
      employeeVisit.endDate,
      parseInt(splitTime[0]),
      parseInt(splitTime[1])
    );
 
    if (startDate > endDate) {
      startDate = endDate;
    }
 
    this.updateEmployeeVisitDates(employeeVisit, startDate, endDate);
  }
 
  updateEmployeeVisitDates(employeeVisit: EmployeeVisit, startDate: Date, endDate: Date) {
    employeeVisit.startDate = startDate;
    employeeVisit.endDate = endDate;
  }
 
  createEmployeeVisits() {
    if (this.employeeVisits.length > 0) {
      const employeeVisitsDto = this.employeeVisits.map((e) => {
        return {
          workOrderId: this.workOrder.id,
          employeeId: e.employeeId,
          startDate: DateService.convertDateToString(e.startDate),
          endDate: DateService.convertDateToString(e.endDate),
        } as EmployeeVisitDto;
      });
 
      if (this.workOrder.id)
        this.workOrderRepository.createWorkOrderEmployeeVisits(this.workOrder.id, employeeVisitsDto).then(() => {
          this.employeeVisitsViewChange.emit(EmployeeVisitsView.List);
        });
    }
  }
 
  changeWizardStep(event: StepperSelectionEvent) {
    if (this.stepper) {
      event.selectedIndex == this.stepper.steps.length - 1
        ? this.enableSaveButton.emit(true)
        : this.enableSaveButton.emit(false);
    }
  }
}